import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { CustomersService } from '@core/services';
import { ClusterPoint, VehicleCluster } from '@gozetta/z-maps/vehicles';
import { catchError, filter, switchMap, take, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { US_BOUNDS } from './constants';
import { LiveMapService } from './services/live-map.service';

/**
 * Fetches initial vehicle clusters using the locations derived from the
 * customer-vehicle links response (instead of the current user's full
 * location set). This keeps the initial call aligned with the filters the
 * component will later apply, avoiding a duplicate cluster fetch right after
 * the page loads.
 *
 * Falls back to an empty array on error so the map still loads.
 */
export const liveMapBoundsResolver: ResolveFn<ClusterPoint[]> = () => {
  const clusterService = inject(VehicleCluster);
  const customerService = inject(CustomersService);
  const liveMapService = inject(LiveMapService);

  return customerService.selectedCustomerIds$.pipe(
    filter((ids): ids is number[] => ids !== null && ids.length > 0),
    take(1),
    switchMap(customerIds =>
      liveMapService.getCustomersVehicleLinks(customerIds)
    ),
    filter(response => Array.isArray(response.data)),
    tap(response => customerService.updateCustomerVehicleLinks(response.data)),
    switchMap(response => {
      const vehicleNames = response.data
        .map(link => link.vehicleLytxId)
        .filter((id): id is string => !!id);
      const locationIds = [
        ...new Set(response.data.flatMap(link => link.vehicleLoctions ?? [])),
      ];
      if (!locationIds.length && !vehicleNames.length) return of([]);
      return clusterService.fetchClusters(
        US_BOUNDS,
        { locationIds, vehicleNames },
        2
      );
    }),
    catchError(() => of([]))
  );
};
